import { Box, Stack, Typography } from '@mui/material';
import { InvoiceBasicForm } from 'features/invoice/invoice-mutable-data-form/invoice-basic-form';
import { useInvoiceDetails } from 'entities/invoice/invoice.hook';
import { InvoiceRowsCard } from 'entities/invoice/invoice-row/invoice-rows-card';
import { AgentCard } from 'entities/agent-card/agent-card';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { ErrorBanner } from 'shared/ui/error-banner';
import { AuditEntityType, AVAILABLE_FOR_CHANGE_CANCEL_STATUSES, INVOICE_STATUS_CLOSED, INVOICE_TYPE_CLIENT } from '@sr/dto';
import { useTitleHook } from 'hooks/page-title.hook';
import { InvoiceDocuments } from 'widgets/invoice/documents/invoice-documents';
import { PageHeader } from 'shared/ui/details-page-header';
import { InvoiceStatusSpan } from 'entities/invoice/status-span/status-span';
import { useInvoiceWcc } from 'features/invoice/invoice-wcc/lib';
import { useIdParam } from 'utils/url-paramters.hook';
import { InvoiceTitle } from 'features/invoice/title/title';
import { useInvoiceBasics } from 'entities/invoice/mutable-data/lib';
import { InvoiceCancelStatus } from 'features/invoice/invoice-cancel-status';
import { AuditLogSection } from 'entities/audit/ui/audit-log-section';
import { InvoiceModeActions } from 'widgets/invoice/mode-actions/invoice-mode-actions';
import { AdvanceInvoice } from 'widgets/invoice/documents/advance-invoice';

export const InvoiceDetailsPage = () => {
  const invoiceId = useIdParam('invoiceId');

  const { data: invoiceBasics } = useInvoiceBasics(invoiceId);
  const { invoice, error } = useInvoiceDetails(invoiceId);

  useTitleHook(invoice && `Счет ${invoice.invoiceNumber} для ${invoice.buyerCompany.name}`);

  const { invoiceWcc } = useInvoiceWcc({ invoiceId, initialWcc: null });

  const allowEditRows = !(invoiceWcc || invoice?.status === INVOICE_STATUS_CLOSED);

  if (!invoice) {
    if (error)
      return <ErrorBanner errorMessage={error} />;

    return <LoadingBanner />;
  }

  return (
    <>
      <PageHeader
        title={<InvoiceTitle invoice={invoice} basics={invoiceBasics} />}
        actions={<InvoiceModeActions invoice={invoice} mode="edit" />}
        subHeader={
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="body1" component="span">Статус: </Typography>
              <InvoiceStatusSpan status={invoice.status} size="medium" />
            </Box>
            {AVAILABLE_FOR_CHANGE_CANCEL_STATUSES.includes(invoice.status) &&
              <InvoiceCancelStatus invoiceId={invoice.id} />}
          </Stack>
        }
      />

      <Stack direction="row" spacing={2} alignContent="flex-end" justifyItems="center">
        <Box>
          <InvoiceBasicForm allowEdit={allowEditRows} invoice={invoice} />
        </Box>

        <Box>
          <Stack direction="column" spacing={2}>
            <AgentCard title="Покупатель" company={invoice.buyerCompany} legalEntity={invoice.buyer} />
            <AgentCard title="Продавец" company={invoice.sellerCompany} legalEntity={invoice.seller} />
          </Stack>
        </Box>

        <Box>
          <Stack direction="column" spacing={2}>
            <InvoiceDocuments invoiceId={invoice.id} />
            {invoice.type === INVOICE_TYPE_CLIENT ? <AdvanceInvoice invoiceId={invoice.id} /> : ''}
          </Stack>
        </Box>
      </Stack>

      <Box mt={2}>
        <InvoiceRowsCard
          invoiceId={invoice.id}
          allowEdit={allowEditRows}
          isClientInvoice={invoice.type === INVOICE_TYPE_CLIENT}
          seller={invoice.seller} />
      </Box>

      <AuditLogSection entityType={AuditEntityType.INVOICE} entityId={invoice.id} />
    </>
  );
};

export default InvoiceDetailsPage;
